import {currentSession} from "./Auth";
import {
    AppUser,
    Charge,
    Client, ClientDocument,
    Document, EsignDocument,
    EsignProfile,
    Feature, FileResponse, FileShare,
    Key, LegacyDocument, LegacyNote, Payment,
    Template,
    TherapySession
} from "./types";


const coreApiEndpoint = (window.location.hostname === "localhost")
    ? (window.location.protocol + '//' + window.location.hostname + ":8090/promises12/api")
    : (window.location.protocol + '//' + window.location.host + "/promises12/api");

let esignApiEndpoint = (window.location.protocol + '//' + window.location.hostname + ":8088/esign/api")
if (window.location.hostname === "34.201.190.181") {
    esignApiEndpoint = "http://34.201.190.181:3005/esign/api";
}



if (window.location.hostname === "test.promises12data.com" || window.location.hostname === "promises12data.com") {
    esignApiEndpoint = "https://esign.promises12data.com/esign/api";
}

enum HttpMethod {
    GET = "GET", HEAD = "HEAD", POST = "POST", PUT = "PUT", PATCH = "PATCH", DELETE = "DELETE", OPTIONS = "OPTIONS", TRACE = "TRACE"
}

// const serialize = (obj: any) => {
//     const str = [];
//     for (const p in obj)
//         if (obj.hasOwnProperty(p)) {
//             if (obj[p] && obj[p].length > 0) {
//                 str.push(encodeURIComponent(p) + "=" + encodeURIComponent(obj[p]));
//             }
//         }
//     return str.join("&");
// }

function handleResponse(response: Response) {
    if (response.ok) {
        return response.json();
    } else {
        return response.json().then(function (error) {
            throw error;
        });
    }
}


function handleNetworkError(error: Error) {
    throw error;
}

//

type getEsignDocument = (documentId: string, profileId: string, documentKey: string) => Promise<EsignDocument>;

export const getEsignDocument: getEsignDocument = (async (documentId, profileId, documentKey) => {

    return fetch(esignApiEndpoint + "/profiles/" + profileId + "/documents/" + documentId + "?key=" + documentKey, {
        mode: 'cors',
        headers: {
            Pragma: 'no-cache'
        },
        method: HttpMethod.GET
    }).then(handleResponse, handleNetworkError);
});


//

type getDocumentKey = (profileId: string, documentId: string) => Promise<Key>;

export const getDocumentKey: getDocumentKey = (async (profileId, documentId) => {
    const userSession = await currentSession();
    return fetch(esignApiEndpoint + "/profiles/" + profileId + "/documents/" + documentId + "/keys", {
        mode: 'cors',
        credentials: 'include',
        headers: {
            Authorization: 'Bearer ' + userSession.access_token,
            Pragma: 'no-cache'
        },
        method: HttpMethod.POST
    }).then(handleResponse, handleNetworkError);
});

type getCustomerDocumentKey = (profileId: string, documentId: string) => Promise<Key>;

export const getCustomerDocumentKey: getCustomerDocumentKey = (async (profileId, documentId) => {
    const userSession = await currentSession();
    return fetch(esignApiEndpoint + "/profiles/" + profileId + "/documents/" + documentId + "/customer-keys", {
        mode: 'cors',
        credentials: 'include',
        headers: {
            Authorization: 'Bearer ' + userSession.access_token,
            Pragma: 'no-cache'
        },
        method: HttpMethod.POST
    }).then(handleResponse, handleNetworkError);
});

type getDocumentKeyPublic = (profileId: string, documentId: string, key: string) => Promise<Key>;

export const getDocumentKeyPublic: getDocumentKeyPublic = (async (profileId, documentId, key) => {
    return fetch(esignApiEndpoint + "/profiles/" + profileId + "/documents/" + documentId + "/keys", {
        mode: 'cors',
        credentials: 'include',
        headers: {
            Authorization: 'Bearer ' + key,
            Pragma: 'no-cache'
        },
        method: HttpMethod.POST
    }).then(handleResponse, handleNetworkError);
});


//

type getTemplates = (profileId: string) => Promise<Template[]>;

export const getTemplates: getTemplates = (async (profileId) => {
    const userSession = await currentSession();
    return fetch(esignApiEndpoint + "/profiles/" + profileId + "/templates", {
        mode: 'cors',
        credentials: 'include',
        headers: {
            Authorization: 'Bearer ' + userSession.access_token,
            Pragma: 'no-cache'
        },
        method: HttpMethod.GET
    }).then(handleResponse, handleNetworkError);
});

//

type CreateDocumentBody = {
    template: {
        uuid: string
    },
    fields?: {
        name: string,
        value: string
    }[]
}

type createDocument = (profileId: string, document: CreateDocumentBody) => Promise<Document>;

export const createDocument: createDocument = (async (profileId, document) => {
    const userSession = await currentSession();
    return fetch(esignApiEndpoint + "/profiles/" + profileId + "/documents", {
        mode: 'cors',
        credentials: 'include',
        headers: {
            Authorization: 'Bearer ' + userSession.access_token,
            Pragma: 'no-cache',
            'Content-Type': 'application/json'
        },
        method: HttpMethod.POST,
        body: JSON.stringify(document)
    }).then(handleResponse, handleNetworkError);
});

//

type getEsignProfiles = () => Promise<EsignProfile[]>;

export const getEsignProfiles: getEsignProfiles = (async () => {
    const userSession = await currentSession();
    return fetch(esignApiEndpoint + "/profiles/me", {
        mode: 'cors',
        credentials: 'include',
        headers: {
            Authorization: 'Bearer ' + userSession.access_token,
            Pragma: 'no-cache'
        },
        method: HttpMethod.GET
    }).then(handleResponse, handleNetworkError);

});

type getEsignProfilesPublic = (key: string) => Promise<EsignProfile[]>;

export const getEsignProfilesPublic: getEsignProfilesPublic = (async (key) => {
    return fetch(esignApiEndpoint + "/profiles/me", {
        mode: 'cors',
        credentials: 'include',
        headers: {
            Authorization: 'Bearer ' + key,
            Pragma: 'no-cache'
        },
        method: HttpMethod.GET
    }).then(handleResponse, handleNetworkError);
});

type EsignFile = {
    uuid: string,
    name: string
}

// Uploads a signature image to the esign file store. The uploaded file is served by the
// esign nginx at /esign-files/{uuid}/{name}, which is the url stored on the esign user.
type uploadEsignSignature = (file: Blob, fileName: string) => Promise<EsignFile>;

export const uploadEsignSignature: uploadEsignSignature = (async (file, fileName) => {
    const userSession = await currentSession();
    const formData = new FormData();
    formData.append("file", file, fileName);
    return fetch(esignApiEndpoint + "/files/upload", {
        mode: 'cors',
        credentials: 'include',
        headers: {
            Authorization: 'Bearer ' + userSession.access_token
        },
        method: HttpMethod.POST,
        body: formData
    }).then(handleResponse, handleNetworkError);
});

// Creates a new document template in esign by uploading a PDF. The endpoint persists the file and
// associates it with the template. tag must be one of "form" or "note".
type addEsignTemplate = (name: string, tag: string, file: File) => Promise<Template>;

export const addEsignTemplate: addEsignTemplate = (async (name, tag, file) => {
    const userSession = await currentSession();
    const formData = new FormData();
    formData.append("name", name);
    formData.append("tag", tag);
    formData.append("file", file, file.name);
    return fetch(esignApiEndpoint + "/templates", {
        mode: 'cors',
        credentials: 'include',
        headers: {
            Authorization: 'Bearer ' + userSession.access_token
        },
        method: HttpMethod.POST,
        body: formData
    }).then(handleResponse, handleNetworkError);
});

type AddEsignUserBody = {
    email: string,
    role: string,
    signature: string,
    activated: boolean
}

// Creates the user in the esign api. The profile is derived from the authenticated caller.
type addEsignUser = (body: AddEsignUserBody) => Promise<any>;

export const addEsignUser: addEsignUser = (async (body) => {
    const userSession = await currentSession();
    return fetch(esignApiEndpoint + "/users/", {
        mode: 'cors',
        credentials: 'include',
        headers: {
            Authorization: 'Bearer ' + userSession.access_token,
            Pragma: 'no-cache',
            'Content-Type': 'application/json'
        },
        method: HttpMethod.POST,
        body: JSON.stringify(body)
    }).then(handleResponse, handleNetworkError);
});

//

type getUserProfile = () => Promise<AppUser>;

export const getUserProfile: getUserProfile = (async () => {
    const userSession = await currentSession();
    return fetch(coreApiEndpoint + "/users/me", {
        mode: 'cors',
        credentials: 'include',
        headers: {
            Authorization: 'Bearer ' + userSession.access_token,
            Pragma: 'no-cache'
        },
        method: HttpMethod.GET
    }).then(handleResponse, handleNetworkError);
});

type uploadFile = (clientId: string, file: File) => Promise<FileResponse>;

export const uploadFile: uploadFile = (async (clientId, file) => {
    const userSession = await currentSession();
    const formData = new FormData();
    formData.append(
        "file",
        file,
        file.name
    );
    return fetch(coreApiEndpoint + "/clients/" + clientId + "/files", {
        mode: 'cors',
        credentials: 'include',
        headers: {
            Authorization: 'Bearer ' + userSession.access_token
        },
        method: HttpMethod.POST,
        body: formData
    }).then(handleResponse, handleNetworkError);
});

type getFile = (path: string) => Promise<Response>;

export const getFile: getFile = (async (path) => {
    const userSession = await currentSession();
    return fetch(coreApiEndpoint + path, {
        mode: 'cors',
        credentials: 'include',
        headers: {
            Authorization: 'Bearer ' + userSession.access_token
        },
        method: HttpMethod.GET
    });
});

type getFilePublic = (path: string, key: string) => Promise<Response>;

export const getFilePublic: getFilePublic = (async (path, key) => {
    return fetch(coreApiEndpoint + path, {
        mode: 'cors',
        credentials: 'include',
        headers: {
            Authorization: 'Bearer ' + key
        },
        method: HttpMethod.GET
    });
});

type getLegacyDocument = (id: number) => Promise<LegacyDocument>;

export const getLegacyDocument: getLegacyDocument = (async (id) => {
    const userSession = await currentSession();
    return fetch(coreApiEndpoint + "/legacy-documents/" + id, {
        mode: 'cors',
        credentials: 'include',
        headers: {
            Authorization: 'Bearer ' + userSession.access_token,
            Pragma: 'no-cache'
        },
        method: HttpMethod.GET
    }).then(handleResponse, handleNetworkError);
});

type getLegacyNote = (id: number) => Promise<LegacyNote>;

export const getLegacyNote: getLegacyNote = (async (id) => {
    const userSession = await currentSession();
    return fetch(coreApiEndpoint + "/legacy-notes/" + id, {
        mode: 'cors',
        credentials: 'include',
        headers: {
            Authorization: 'Bearer ' + userSession.access_token,
            Pragma: 'no-cache'
        },
        method: HttpMethod.GET
    }).then(handleResponse, handleNetworkError);
});

type getLegacyDocumentPublic = (id: number, key: string) => Promise<LegacyDocument>;

export const getLegacyDocumentPublic: getLegacyDocumentPublic = (async (id, key) => {
    return fetch(coreApiEndpoint + "/legacy-documents/" + id, {
        mode: 'cors',
        credentials: 'include',
        headers: {
            Authorization: 'Bearer ' + key,
            Pragma: 'no-cache'
        },
        method: HttpMethod.GET
    }).then(handleResponse, handleNetworkError);
});

type getUsers = () => Promise<AppUser[]>;

export const getUsers: getUsers = (async () => {
    const userSession = await currentSession();
    return fetch(coreApiEndpoint + "/users", {
        mode: 'cors',
        credentials: 'include',
        headers: {
            Authorization: 'Bearer ' + userSession.access_token,
            Pragma: 'no-cache'
        },
        method: HttpMethod.GET
    }).then(handleResponse, handleNetworkError);
});

type AddUserBody = {
    email: string,
    password: string,
    firstName: string,
    lastName: string,
    role: string
}

type addUser = (body: AddUserBody) => Promise<AppUser>;

export const addUser: addUser = (async (body) => {
    const userSession = await currentSession();
    return fetch(coreApiEndpoint + "/users", {
        mode: 'cors',
        credentials: 'include',
        headers: {
            Authorization: 'Bearer ' + userSession.access_token,
            Pragma: 'no-cache',
            'Content-Type': 'application/json'
        },
        method: HttpMethod.POST,
        body: JSON.stringify(body)
    }).then(handleResponse, handleNetworkError);
});

type getFeatures = () => Promise<Feature[]>;

export const getFeatures: getFeatures = (async () => {
    const userSession = await currentSession();
    return fetch(coreApiEndpoint + "/features", {
        mode: 'cors',
        credentials: 'include',
        headers: {
            Authorization: 'Bearer ' + userSession.access_token,
            Pragma: 'no-cache'
        },
        method: HttpMethod.GET
    }).then(handleResponse, handleNetworkError);
});


type getClients = (clientId: string, firstName: string, lastName: string, email: string, disabled: string, therapySessionId: string) => Promise<Client[]>;

export const getClients: getClients = (async (clientId, firstName, lastName, email, disabled, therapySessionId) => {
    const userSession = await currentSession();
    return fetch(coreApiEndpoint + "/clients?id=" + clientId + "&firstName=" + firstName + "&lastName=" + lastName
        + "&email=" + email + "&disabled=" + disabled + "&therapySessionId=" + therapySessionId, {
        mode: 'cors',
        credentials: 'include',
        headers: {
            Authorization: 'Bearer ' + userSession.access_token,
            Pragma: 'no-cache'
        },
        method: HttpMethod.GET,
    }).then(handleResponse, handleNetworkError);
});

type getClient = (id: string) => Promise<Client>;

export const getClient: getClient = (async (id) => {
    const userSession = await currentSession();
    return fetch(coreApiEndpoint + "/clients/" + id, {
        mode: 'cors',
        credentials: 'include',
        headers: {
            Authorization: 'Bearer ' + userSession.access_token,
            Pragma: 'no-cache'
        },
        method: HttpMethod.GET,
    }).then(handleResponse, handleNetworkError);
});

type createClientShare = (id: string) => Promise<FileShare>;

export const createClientShare: createClientShare = (async (id) => {
    const userSession = await currentSession();
    return fetch(coreApiEndpoint + "/clients/" + id + "/share", {
        mode: 'cors',
        credentials: 'include',
        headers: {
            Authorization: 'Bearer ' + userSession.access_token,
            Pragma: 'no-cache'
        },
        method: HttpMethod.POST,
    }).then(handleResponse, handleNetworkError);
});

type getClientPublic = (id: string, key: string) => Promise<Client>;

export const getClientPublic: getClientPublic = (async (id, key) => {
    return fetch(coreApiEndpoint + "/clients/" + id, {
        mode: 'cors',
        credentials: 'include',
        headers: {
            Authorization: 'Bearer ' + key,
            Pragma: 'no-cache'
        },
        method: HttpMethod.GET,
    }).then(handleResponse, handleNetworkError);
});

type getTherapySessions = (therapistId: string, fromDate: string, toDate: string) => Promise<TherapySession[]>;

export const getTherapySessions: getTherapySessions = (async (therapistId, fromDate, toDate) => {
    const userSession = await currentSession();
    return fetch(coreApiEndpoint + "/therapy-sessions?therapistId=" + therapistId + "&fromDate=" + fromDate
        + "&toDate=" + toDate, {
        mode: 'cors',
        credentials: 'include',
        headers: {
            Authorization: 'Bearer ' + userSession.access_token,
            Pragma: 'no-cache'
        },
        method: HttpMethod.GET,
    }).then(handleResponse, handleNetworkError);
});

type getTherapySession = (id: string) => Promise<TherapySession>;

export const getTherapySession: getTherapySession = (async (id) => {
    const userSession = await currentSession();
    return fetch(coreApiEndpoint + "/therapy-sessions/" + id, {
        mode: 'cors',
        credentials: 'include',
        headers: {
            Authorization: 'Bearer ' + userSession.access_token,
            Pragma: 'no-cache'
        },
        method: HttpMethod.GET,
    }).then(handleResponse, handleNetworkError);
});

type deleteTherapySession = (id: string) => Promise<Response>;

export const deleteTherapySession: deleteTherapySession = (async (id) => {
    const userSession = await currentSession();
    return fetch(coreApiEndpoint + "/therapy-sessions/" + id, {
        mode: 'cors',
        credentials: 'include',
        headers: {
            Authorization: 'Bearer ' + userSession.access_token,
            Pragma: 'no-cache'
        },
        method: HttpMethod.DELETE,
    });
});

type completeTherapySession = (id: string) => Promise<Response>;

export const completeTherapySession: completeTherapySession = (async (id) => {
    const userSession = await currentSession();
    return fetch(coreApiEndpoint + "/therapy-sessions/" + id + "/complete", {
        mode: 'cors',
        credentials: 'include',
        headers: {
            Authorization: 'Bearer ' + userSession.access_token,
            Pragma: 'no-cache'
        },
        method: HttpMethod.POST,
    });
});

type addClientToTherapySession = (therapySessionId: string, clientId: string) => Promise<Response>;

export const addClientToTherapySession: addClientToTherapySession = (async (therapySessionId, clientId) => {
    const userSession = await currentSession();
    return fetch(coreApiEndpoint + "/therapy-sessions/" + therapySessionId + "/clients/" + clientId, {
        mode: 'cors',
        credentials: 'include',
        headers: {
            Authorization: 'Bearer ' + userSession.access_token,
            Pragma: 'no-cache'
        },
        method: HttpMethod.POST,
    });
});

type deleteClientToTherapySession = (therapySessionId: string, clientId: string) => Promise<Response>;

export const deleteClientToTherapySession: deleteClientToTherapySession = (async (therapySessionId, clientId) => {
    const userSession = await currentSession();
    return fetch(coreApiEndpoint + "/therapy-sessions/" + therapySessionId + "/clients/" + clientId, {
        mode: 'cors',
        credentials: 'include',
        headers: {
            Authorization: 'Bearer ' + userSession.access_token,
            Pragma: 'no-cache'
        },
        method: HttpMethod.DELETE,
    });
});

type markAttendanceForClientInTherapySession = (therapySessionId: string, clientId: string, attended: boolean, documentId?: string) => Promise<Response>;

export const markAttendanceForClientInTherapySession: markAttendanceForClientInTherapySession = (async (therapySessionId, clientId, attendance, documentId) => {
    const userSession = await currentSession();
    return fetch(coreApiEndpoint + "/therapy-sessions/" + therapySessionId + "/clients/" + clientId + "/mark-attendance", {
        mode: 'cors',
        credentials: 'include',
        headers: {
            Authorization: 'Bearer ' + userSession.access_token,
            Pragma: 'no-cache',
            'Content-Type': 'application/json'
        },
        method: HttpMethod.POST,
        body: JSON.stringify({
            attended: attendance,
            document: {
                uuid: documentId
            }
        })
    });
});

type resetAttendanceForClientInTherapySession = (therapySessionId: string, clientId: string) => Promise<Response>;

export const resetAttendanceForClientInTherapySession: resetAttendanceForClientInTherapySession = (async (therapySessionId, clientId) => {
    const userSession = await currentSession();
    return fetch(coreApiEndpoint + "/therapy-sessions/" + therapySessionId + "/clients/" + clientId + "/reset-attendance", {
        mode: 'cors',
        credentials: 'include',
        headers: {
            Authorization: 'Bearer ' + userSession.access_token,
            Pragma: 'no-cache'
        },
        method: HttpMethod.POST
    });
});

type AddTherapySessionBody = {
    therapist: {
        uuid: string
    },
    date: string,
    startTime: string,
    endTime: string,
    noteUuid: string
}

type addTherapySessions = (body: AddTherapySessionBody) => Promise<TherapySession>;

export const addTherapySessions: addTherapySessions = (async (body) => {
    const userSession = await currentSession();
    return fetch(coreApiEndpoint + "/therapy-sessions", {
        mode: 'cors',
        credentials: 'include',
        headers: {
            Authorization: 'Bearer ' + userSession.access_token,
            Pragma: 'no-cache',
            'Content-Type': 'application/json'
        },
        method: HttpMethod.POST,
        body: JSON.stringify(body)
    }).then(handleResponse, handleNetworkError);
});

type updateTherapySessions = (id: string, body: AddTherapySessionBody) => Promise<TherapySession>;

export const updateTherapySessions: updateTherapySessions = (async (id, body) => {
    const userSession = await currentSession();
    return fetch(coreApiEndpoint + "/therapy-sessions/" + id, {
        mode: 'cors',
        credentials: 'include',
        headers: {
            Authorization: 'Bearer ' + userSession.access_token,
            Pragma: 'no-cache',
            'Content-Type': 'application/json'
        },
        method: HttpMethod.PUT,
        body: JSON.stringify(body)
    }).then(handleResponse, handleNetworkError);
});

type AddClientBody = {
    address: {
        addressLine1: string,
        addressLine2: string,
        city: string,
        state: string,
        zip: string,
        uuid?: string
    },
    firstName: string,
    lastName: string,
    socialsecurityNumber: string,
    driverslicenseNumber: string,
    driverslicenseState: string,
    dateofBirth: string,
    telephoneNumber: string,
    email: string,
    dateTimeofAssessment: string,
    recommendedHours?: string,
    clientDiagnosis: string,
    drugTestDueDate: string
}

type addClient = (body: AddClientBody) => Promise<Client>;

export const addClient: addClient = (async (body) => {
    const userSession = await currentSession();
    return fetch(coreApiEndpoint + "/clients", {
        mode: 'cors',
        credentials: 'include',
        headers: {
            Authorization: 'Bearer ' + userSession.access_token,
            Pragma: 'no-cache',
            'Content-Type': 'application/json'
        },
        method: HttpMethod.POST,
        body: JSON.stringify(body)
    }).then(handleResponse, handleNetworkError);
});

type updateClient = (id: string, body: AddClientBody) => Promise<Client>;

export const updateClient: updateClient = (async (id, body) => {
    const userSession = await currentSession();
    return fetch(coreApiEndpoint + "/clients/" + id, {
        mode: 'cors',
        credentials: 'include',
        headers: {
            Authorization: 'Bearer ' + userSession.access_token,
            Pragma: 'no-cache',
            'Content-Type': 'application/json'
        },
        method: HttpMethod.PUT,
        body: JSON.stringify(body)
    }).then(handleResponse, handleNetworkError);
});

type disableClient = (id: string) => Promise<Response>;

export const disableClient: disableClient = (async (id) => {
    const userSession = await currentSession();
    return fetch(coreApiEndpoint + "/clients/" + id + "/disable", {
        mode: 'cors',
        credentials: 'include',
        headers: {
            Authorization: 'Bearer ' + userSession.access_token,
            Pragma: 'no-cache',
            'Content-Type': 'application/json'
        },
        method: HttpMethod.POST
    });
});

type enableClient = (id: string) => Promise<Response>;

export const enableClient: enableClient = (async (id) => {
    const userSession = await currentSession();
    return fetch(coreApiEndpoint + "/clients/" + id + "/enable", {
        mode: 'cors',
        credentials: 'include',
        headers: {
            Authorization: 'Bearer ' + userSession.access_token,
            Pragma: 'no-cache',
            'Content-Type': 'application/json'
        },
        method: HttpMethod.POST
    });
});

type AddChargeBody = {
    amount: string,
    description: string,
    chargeDate: string
}

type addCharge = (clientId: string, body: AddChargeBody) => Promise<Charge>;

export const addCharge: addCharge = (async (clientId, body) => {
    const userSession = await currentSession();
    return fetch(coreApiEndpoint + "/clients/" + clientId + "/charges", {
        mode: 'cors',
        credentials: 'include',
        headers: {
            Authorization: 'Bearer ' + userSession.access_token,
            Pragma: 'no-cache',
            'Content-Type': 'application/json'
        },
        method: HttpMethod.POST,
        body: JSON.stringify(body)
    }).then(handleResponse, handleNetworkError);
});

type getCharge = (clientId: string, chargeId: string) => Promise<Charge>;

export const getCharge: getCharge = (async (clientId, chargeId) => {
    const userSession = await currentSession();
    return fetch(coreApiEndpoint + "/clients/" + clientId + "/charges/" + chargeId, {
        mode: 'cors',
        credentials: 'include',
        headers: {
            Authorization: 'Bearer ' + userSession.access_token,
            Pragma: 'no-cache',
            'Content-Type': 'application/json'
        },
        method: HttpMethod.GET
    }).then(handleResponse, handleNetworkError);
});

type deleteCharge = (clientId: string, chargeId: string) => Promise<Response>;

export const deleteCharge: deleteCharge = (async (clientId, chargeId) => {
    const userSession = await currentSession();
    return fetch(coreApiEndpoint + "/clients/" + clientId + "/charges/" + chargeId, {
        mode: 'cors',
        credentials: 'include',
        headers: {
            Authorization: 'Bearer ' + userSession.access_token,
            Pragma: 'no-cache',
            'Content-Type': 'application/json'
        },
        method: HttpMethod.DELETE
    });
});

//

type AddPaymentBody = {
    amount: string,
    description: string,
    paymentDate: string,
    method: string,
    status: string,
    creditCard?: {
        uuid?: string,
        creditCardNumber?: string,
        cvv?: string,
        expirationMonth?: string,
        expirationYear?: string,
        zipCode?: string
    }
}

type addPayment = (clientId: string, body: AddPaymentBody) => Promise<Payment>;

export const addPayment: addPayment = (async (clientId, body) => {
    const userSession = await currentSession();
    return fetch(coreApiEndpoint + "/clients/" + clientId + "/payments", {
        mode: 'cors',
        credentials: 'include',
        headers: {
            Authorization: 'Bearer ' + userSession.access_token,
            Pragma: 'no-cache',
            'Content-Type': 'application/json'
        },
        method: HttpMethod.POST,
        body: JSON.stringify(body)
    }).then(handleResponse, handleNetworkError);
});

type resendPaymentReceipt = (clientId: string, paymentId: string) => Promise<Response>;

export const sendPaymentReceipt: resendPaymentReceipt = (async (clientId, paymentId) => {
    const userSession = await currentSession();
    return fetch(coreApiEndpoint + "/clients/" + clientId + "/payments/" + paymentId + "/send-receipt", {
        mode: 'cors',
        credentials: 'include',
        headers: {
            Authorization: 'Bearer ' + userSession.access_token,
            Pragma: 'no-cache',
        },
        method: HttpMethod.POST,
    });
});

type getPayment = (clientId: string, paymentId: string) => Promise<Payment>;

export const getPayment: getPayment = (async (clientId, paymentId) => {
    const userSession = await currentSession();
    return fetch(coreApiEndpoint + "/clients/" + clientId + "/payments/" + paymentId, {
        mode: 'cors',
        credentials: 'include',
        headers: {
            Authorization: 'Bearer ' + userSession.access_token,
            Pragma: 'no-cache',
            'Content-Type': 'application/json'
        },
        method: HttpMethod.GET
    }).then(handleResponse, handleNetworkError);
});

type deletePayment = (clientId: string, paymentId: string) => Promise<Response>;

export const deletePayment: deletePayment = (async (clientId, paymentId) => {
    const userSession = await currentSession();
    return fetch(coreApiEndpoint + "/clients/" + clientId + "/payments/" + paymentId, {
        mode: 'cors',
        credentials: 'include',
        headers: {
            Authorization: 'Bearer ' + userSession.access_token,
            Pragma: 'no-cache',
            'Content-Type': 'application/json'
        },
        method: HttpMethod.DELETE
    });
});

type AddClientDocumentBody = {
    name: string,
    type: string,
    url: string,
    description: string,
    status: string
}

type addDocumentToClient = (clientId: string, body: AddClientDocumentBody) => Promise<ClientDocument>;

export const addDocumentToClient: addDocumentToClient = (async (clientId, body) => {
    const userSession = await currentSession();
    return fetch(coreApiEndpoint + "/clients/" + clientId + "/documents", {
        mode: 'cors',
        credentials: 'include',
        headers: {
            Authorization: 'Bearer ' + userSession.access_token,
            Pragma: 'no-cache',
            'Content-Type': 'application/json'
        },
        method: HttpMethod.POST,
        body: JSON.stringify(body)
    }).then(handleResponse, handleNetworkError);
});

type getClientDocument = (clientId: string, documentId: string) => Promise<ClientDocument>;

export const getClientDocument: getClientDocument = (async (clientId, documentId) => {
    const userSession = await currentSession();
    return fetch(coreApiEndpoint + "/clients/" + clientId + "/documents/" + documentId, {
        mode: 'cors',
        credentials: 'include',
        headers: {
            Authorization: 'Bearer ' + userSession.access_token,
            Pragma: 'no-cache'
        },
        method: HttpMethod.GET
    }).then(handleResponse, handleNetworkError);
});

type deleteClientDocument = (clientId: string, documentId: string) => Promise<Response>;

export const deleteClientDocument: deleteClientDocument = (async (clientId, documentId) => {
    const userSession = await currentSession();
    return fetch(coreApiEndpoint + "/clients/" + clientId + "/documents/" + documentId, {
        mode: 'cors',
        credentials: 'include',
        headers: {
            Authorization: 'Bearer ' + userSession.access_token,
            Pragma: 'no-cache'
        },
        method: HttpMethod.DELETE
    });
});

type completeDocumentOnClient = (documentId: string) => Promise<Response>;

export const completeDocumentOnClient: completeDocumentOnClient = (async (documentId) => {
    //Remove session
    return fetch(coreApiEndpoint + "/documents/" + documentId + "/complete", {
        mode: 'cors',
        headers: {
            Pragma: 'no-cache'
        },
        method: HttpMethod.POST
    });
});

type PendingCustomerDocumentBody = {
    esignProfileId: string,
    esignDocumentKey: string,
    esignDocumentId: string
}

type pendingCustomerDocumentOnClient = (clientId: string, documentId: string, body: PendingCustomerDocumentBody) => Promise<Response>;

export const pendingCustomerDocumentOnClient: pendingCustomerDocumentOnClient = (async (clientId, documentId, body) => {
    const userSession = await currentSession();
    return fetch(coreApiEndpoint + "/clients/" + clientId + "/documents/" + documentId + "/pending-client-signature", {
        mode: 'cors',
        credentials: 'include',
        headers: {
            Authorization: 'Bearer ' + userSession.access_token,
            Pragma: 'no-cache',
            'Content-Type': 'application/json'
        },
        method: HttpMethod.POST,
        body: JSON.stringify(body)
    });
});

type PendingCustomerReminderBody = {
    esignProfileId: string,
    esignDocumentKey: string,
    esignDocumentId: string
}

type pendingCustomerDocumentOnClientReminder = (clientId: string, documentId: string, body: PendingCustomerReminderBody) => Promise<Response>;

export const pendingCustomerDocumentOnClientReminder: pendingCustomerDocumentOnClientReminder = (async (clientId, documentId, body) => {
    const userSession = await currentSession();
    return fetch(coreApiEndpoint + "/clients/" + clientId + "/documents/" + documentId + "/pending-client-signature-reminder", {
        mode: 'cors',
        credentials: 'include',
        headers: {
            Authorization: 'Bearer ' + userSession.access_token,
            Pragma: 'no-cache',
            'Content-Type': 'application/json'
        },
        method: HttpMethod.POST,
        body: JSON.stringify(body)
    });
});

type shareDocumentWithClient = (clientId: string, documentId: string, body: PendingCustomerReminderBody) => Promise<Response>;

export const shareDocumentWithClient: shareDocumentWithClient = (async (clientId, documentId, body) => {
    const userSession = await currentSession();
    return fetch(coreApiEndpoint + "/clients/" + clientId + "/documents/" + documentId + "/share-document", {
        mode: 'cors',
        credentials: 'include',
        headers: {
            Authorization: 'Bearer ' + userSession.access_token,
            Pragma: 'no-cache',
            'Content-Type': 'application/json'
        },
        method: HttpMethod.POST,
        body: JSON.stringify(body)
    });
});